import { FOUNTAIN_ACTS_ROOT } from '@/data/entities/acts';
import { useCreate, useDelete, useUpdate } from '@/data/mutations';
import type { ResponseType, TextAtoms } from '@/types';

/**
 * The two text-atom URLs, in one place.
 *
 * `scene_note` writes are their own registry family (a note invalidates notes,
 * not scene bodies) but they address the SAME endpoints — and they must, since
 * the nested `text_atoms[]` path silently drops `content_json`. Sharing the
 * builders keeps that "same endpoints, different fan-out" relationship visible
 * rather than leaving two copies of the URL to drift apart.
 */
export const textAtomsEndpoint = ({ project_id }: { project_id: number }) => `projects/${project_id}/text-atoms`;
export const textAtomEndpoint = ({ project_id, id }: { project_id: number; id: number }) => `projects/${project_id}/text-atoms/${id}`;

interface UpdateTextAtomParams {
    project_id: number;
    id: number;
    content?: string;
    tags?: number[];
    content_json?: string | null;
}

interface CreateTextAtomParams {
    project_id: number;
    data: {
        textable_model: string;
        textable_id: number;
        text_atom_type_id: number;
        content: string;
        order?: number;
    };
}

export const useUpdateTextAtom = useUpdate.textAtom<UpdateTextAtomParams, ResponseType<TextAtoms>>({
    endpoint: ({ project_id, id }) => `projects/${project_id}/text-atoms/${id}?_method=PUT`,
    body: ({ content, tags, content_json }) => {
        const body: Partial<Pick<UpdateTextAtomParams, 'content' | 'tags' | 'content_json'>> = {};
        if (content !== undefined) body.content = content;
        if (tags) body.tags = tags;
        if (content_json !== undefined) body.content_json = content_json;
        return body;
    },
});

/**
 * Save synopsis paragraph annotations (`content_json`) on an act/fragment/scene
 * synopsis atom. Acts are not in the textAtom registry row (scene-body saves
 * must not refetch them), so the acts list rides along as an extra here.
 */
export const useUpdateTextAtomAnnotations = useUpdate.textAtom<
    { project_id: number; id: number; content_json: object },
    ResponseType<TextAtoms>
>({
    endpoint: ({ project_id, id }) => `projects/${project_id}/text-atoms/${id}?_method=PUT`,
    body: ({ content_json }) => ({ content_json }),
    extraInvalidates: [FOUNTAIN_ACTS_ROOT],
});

export const useCreateTextAtom = useCreate.textAtom<CreateTextAtomParams, ResponseType<TextAtoms>>({
    endpoint: ({ project_id }) => `projects/${project_id}/text-atoms`,
    body: ({ data }) => data,
});

export const useDeleteTextAtom = useDelete.textAtom<{ project_id: number; id: number }, ResponseType<TextAtoms>>({
    method: 'DELETE',
    endpoint: ({ project_id, id }) => `projects/${project_id}/text-atoms/${id}`,
});
